
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { WizardStepCard } from '../WizardStepCard';
import { SelectField, type DropdownOption, TextField, TextareaField } from '@dopomogai/ui';
import type { AvailableLLM } from '@dopomogai/types';

interface DefineAgentStepProps {
  agentName: string;
  onAgentNameChange: (value: string) => void;
  agentTagline: string;
  onAgentTaglineChange: (value: string) => void;
  availableModels: AvailableLLM[];
  selectedModel: string;
  onModelChange: (value: string) => void;
  websiteDomainsInput: string;
  onWebsiteDomainsInputChange: (value: string) => void;
  websiteDomainsError?: string | null;
  notificationEmail: string;
  onNotificationEmailChange: (value: string) => void;
  notificationEmailError?: string | null;
}

export const DefineAgentStep: React.FC<DefineAgentStepProps> = ({
  agentName,
  onAgentNameChange,
  agentTagline,
  onAgentTaglineChange,
  availableModels,
  selectedModel,
  onModelChange,
  websiteDomainsInput,
  onWebsiteDomainsInputChange,
  websiteDomainsError,
  notificationEmail,
  onNotificationEmailChange,
  notificationEmailError,
}) => {
  const { t } = useTranslation('agentBuilder');

  const modelOptions: DropdownOption[] = useMemo(() => {
    const formatLabel = (m: AvailableLLM) => {
      const id = m.id || '';
      // Remove provider prefix like "openai:" then trim a trailing ":free"
      const noProvider = id.replace(/^[^:]+:/, '');
      const trimmed = noProvider.replace(/:free$/, '');
      const human = m.name || trimmed;
      const isFree = /:free$/.test(id);
      return isFree ? `${human} 🎁 Free` : human;
    };

    return (availableModels ?? []).map(model => ({
      value: model.id,
      label: formatLabel(model),
    }));
  }, [availableModels]);

  return (
    <WizardStepCard
      titleKey="step1.title"
      descriptionKey="step1.description"
      headerBgColor="bg-sky-100"
    >
      <div className="space-y-6">
        <p className="rounded-lg border border-sky-200 bg-sky-50 px-3 py-2 text-xs text-sky-900">
          {t(
            'step1.syncHint',
            'Changes here are shared with the onboarding assistant. Manual edits stay protected unless you explicitly confirm a replacement.',
          )}
        </p>
        <TextField
          label={t('step1.agentName.label')}
          placeholder={t('step1.agentName.placeholder')}
          value={agentName}
          onChange={e => onAgentNameChange(e.target.value)}
          required
          name="agentName"
        />

        <SelectField
          label={t('step1.llmModel.label', 'Language Model')}
          name="model"
          value={selectedModel}
          options={modelOptions}
          placeholder={t('step1.llmModel.selectPlaceholder', 'Select a model...')}
          required
          onChange={newValue => onModelChange(String(newValue))}
        />

        <TextField
          label={t('step1.agentTagline.label')}
          placeholder={t('step1.agentTagline.placeholder')}
          descriptionText={t('step1.agentTagline.description')}
          value={agentTagline}
          onChange={e => onAgentTaglineChange(e.target.value)}
          name="agentTagline"
        />

        <TextareaField
          label={t('step1.websiteDomain.label')}
          placeholder={t('step1.websiteDomain.placeholder')}
          descriptionText={t('step1.websiteDomain.description')}
          value={websiteDomainsInput}
          onChange={e => onWebsiteDomainsInputChange(e.target.value)}
          name="websiteDomains"
          rows={3}
          required
          errorText={websiteDomainsError || undefined}
        />

        <TextField
          label={t('step1.notificationEmail.label')}
          placeholder={t('step1.notificationEmail.placeholder')}
          descriptionText={t('step1.notificationEmail.description')}
          value={notificationEmail}
          onChange={e => onNotificationEmailChange(e.target.value)}
          name="notificationEmail"
          errorText={notificationEmailError || undefined}
        />
      </div>
    </WizardStepCard>
  );
};